import { ReportData } from '../SuitabilityFlow';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useEffect, useMemo, useState } from 'react';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { Flex, Tabs, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useFunds } from 'src/company/hooks/useFunds';
import { useCompanyDocuments } from 'src/company/hooks/useCompanyDocuments';
import { InlineClickableText } from 'src/common/components/inline-clickable-text/InlineClickableText';
import { ReportTypeToLabel } from 'src/company/funds/components/FundDocumentUploadModal';
import {
  GenerateSuitabilityReportPayload,
  useGenerateSuitabilityReport,
} from 'src/suitability-report/hooks/useGenerateSuitabilityReport';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { useSuitability } from 'src/suitability-report/SuitabilityProvider';
import { useFlags } from 'src/feature-flags/FlagsHook';

const DOCX_FILE_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const PDF_FILE_TYPE = 'application/pdf';

export default function SelectAppendicesStep(props: StepProps<ReportData>) {
  const selectedFileIds = props.data.selectedAppendixIds;
  const setSelectedFileIds = (selectedAppendixIds: number[], temporaryAppendixIds?: number[]) => {
    const tempFileIds = temporaryAppendixIds || filesUploadedInFlow.map(f => f.file.id);
    props.setData({ ...props.data, selectedAppendixIds, temporaryAppendixIds: tempFileIds });
  };
  const { data } = props;
  const { files, isLoading } = useFileUploads({
    clientId: data.client?.id,
  });

  const { funds, isLoading: isFundsLoading } = useFunds({ clientId: props.data?.client?.id });
  const [filesUploadedInFlow, setFilesUploadedInFlow] = useState<UploadedFile[]>([]);

  const { generateSuitabilityReport, isLoading: isGeneratingReport } =
    useGenerateSuitabilityReport();

  const { flowId } = useSuitability();
  const { flagEnabledCompanyDocumentsAppendices } = useFlags();

  const { documents: companyDocuments, isLoading: isCompanyDocumentsLoading } =
    useCompanyDocuments();

  useEffect(() => {
    const latestFundFiles = funds
      ?.flatMap(fund => fund.fundFiles.map(file => file))
      .filter(file => file.isLatest)
      .map(f => f.file.id);

    const newSelectedFileIds = selectedFileIds.concat(latestFundFiles);
    setSelectedFileIds(Array.from(new Set(newSelectedFileIds)));
  }, [funds]);

  const tabGroups = useMemo(() => {
    const filteredFiles = files.filter(file =>
      [DOCX_FILE_TYPE, PDF_FILE_TYPE].includes(file.file.fileType),
    );

    const otherClientDocumentsGroup = filteredFiles
      .map(file => ({
        id: file.file.id,
        title: file.file.fileName,
        right: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
        checked: selectedFileIds.includes(file.file.id),
      }))
      .filter(f => !filesUploadedInFlow.map(f => f.file.id).includes(f.id));

    const fundDocumentsGroup =
      funds
        ?.flatMap(fund =>
          fund.fundFiles.map(file => ({
            id: file.file.id,
            title: fund.name,
            right: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
            checked: selectedFileIds.includes(file.file.id),
            primaryPill: file.isLatest ? 'Latest' : null,
            secondaryPill: ReportTypeToLabel[file.reportType],
            type: file.reportType,
          })),
        )
        .sort(a => (a.primaryPill ? -1 : 1)) || [];

    const fundFactSheetDocuments = fundDocumentsGroup.filter(doc => doc.type === 'FUND_FACT_SHEET');

    const companyDocumentsGroup = flagEnabledCompanyDocumentsAppendices
      ? companyDocuments
          .filter(
            doc => !doc.isArchived && [DOCX_FILE_TYPE, PDF_FILE_TYPE].includes(doc.file.fileType),
          )
          .map(doc => ({
            id: doc.file.id,
            title: doc.file.fileName,
            right: `Uploaded: ${new Date(doc.createdAt).toLocaleDateString()}`,
            checked: selectedFileIds.includes(doc.file.id),
          }))
      : [];

    const onlyForThisReportGroup = filesUploadedInFlow.map(file => ({
      id: file.file.id,
      title: file.file.fileName,
      right: `Uploaded: now`,
      checked: selectedFileIds.includes(file.file.id),
    }));

    const allDocs = [
      ...onlyForThisReportGroup,
      ...fundDocumentsGroup,
      ...companyDocumentsGroup,
      ...otherClientDocumentsGroup,
    ].sort(a => ('primaryPill' in a && a.primaryPill ? -1 : 1));

    return {
      all: allDocs,
      fundFactSheets: [...fundFactSheetDocuments],
      companyDocuments: [...companyDocumentsGroup],
      other: [...otherClientDocumentsGroup],
    };
  }, [
    files,
    funds,
    filesUploadedInFlow,
    selectedFileIds,
    companyDocuments,
    flagEnabledCompanyDocumentsAppendices,
  ]);

  const { uploadFiles } = useFileUploadActions({ clientId: data.client?.id });
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList) {
      setIsUploading(true);
      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
          isTemporary: true,
        }));
        const result = await uploadFiles(payload);
        const newUploadedFiles = result.map(r => r.data);
        const newUploadedFileIds: number[] = result.map(response => response.data.file.id);
        const currentTempIds = filesUploadedInFlow.map(f => f.file.id);
        const updatedTempIds = [...currentTempIds, ...newUploadedFileIds];

        setFilesUploadedInFlow(prev => [...prev, ...newUploadedFiles]);
        setSelectedFileIds([...selectedFileIds, ...newUploadedFileIds], updatedTempIds);
      } catch (error) {
        notifyAndLogError(
          'Failed to upload files',
          'An error occurred while uploading files. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }

    event.target.value = '';
  };

  const handleGenerateReport = async () => {
    if (data.selectionType === 'client' && (!data.client || !data.client.id)) {
      notifyAndLogError(
        "Something doesn't look right",
        "We're missing some client data in order to proceed. Please try from the beginning of the flow and if the problem persists, contact support.",
        new Error('Client not selected'),
      );
      return;
    }
    if (data.selectionType === 'family' && !data.family) {
      notifyAndLogError(
        "Something doesn't look right",
        "We're missing some family data in order to proceed. Please try from the beginning of the flow and if the problem persists, contact support.",
        new Error('Family not selected'),
      );
      return;
    }
    const id = data.selectionType === 'client' ? data.client!.id : data.family!.id;
    const payload: GenerateSuitabilityReportPayload = {
      reportTemplate: props.data.selectedTemplateId,
      supportingDataUploadFileIds: props.data.selectedDocumentIds,
      feeDocumentFileIds: props.data.selectedFeeDocumentIds,
      appendixFileIds: props.data.selectedAppendixIds,
      useDeepThinking: props.data.useDeepThinking,
      reportFormat: props.data.reportFormat,
    };

    if (props.data.enableLastReview) {
      payload.lastReviewDate = props.data.lastClientReview?.toISOString();
    }

    try {
      await generateSuitabilityReport(id, data.selectionType, payload);
      Mixpanel.track(MixpanelEvents.SUITABILITY_REPORT_FLOW_APPENDICES_SELECTED_AND_GENERATE, {
        ...data,
        flowId,
      });
      props.onSuccess();
    } catch (error) {
      notifyAndLogError('Error', 'Failed to generate report', error);
    }
  };

  const showOnlyAllTab = Object.values(tabGroups).filter(g => g.length > 0).length <= 2;
  return (
    <DrawerFlowScreenLayout
      {...props}
      title={'Add appendices'}
      description={
        'Confirm, select more or upload documents to include in the appendix to the report.'
      }
      showClientName={false}
      isLoading={isLoading || isFundsLoading || isCompanyDocumentsLoading || isUploading}
      continueText={isGeneratingReport ? 'Generating report' : 'Generate Report'}
      canGoForward={!isLoading}
      canGoBack={!isLoading}
      nextStep={handleGenerateReport}
      isSubmitting={isLoading}
    >
      <Flex direction="column" gap="xs" mih={0} flex={1} h="100%">
        <Tabs
          defaultValue="all"
          variant={'section'}
          display="flex"
          flex={1}
          mih={0}
          style={{ flexDirection: 'column' }}
        >
          <Tabs.List style={{ flexShrink: 0 }}>
            <Tabs.Tab value="all">All</Tabs.Tab>
            {tabGroups.fundFactSheets.length > 0 && !showOnlyAllTab && (
              <Tabs.Tab value="fund">Fund fact sheets</Tabs.Tab>
            )}
            {tabGroups.companyDocuments.length > 0 && !showOnlyAllTab && (
              <Tabs.Tab value="company">Company appendices</Tabs.Tab>
            )}
            {tabGroups.other.length > 0 && !showOnlyAllTab && (
              <Tabs.Tab value="other">Other</Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="all" flex={1} mih={0}>
            <Flex mt={'md'} flex={1} mih={0} direction="column">
              <SelectableItemList
                data-testid="selectable-list-all"
                items={tabGroups.all}
                selectedItemIds={selectedFileIds}
                setSelectedItemIds={setSelectedFileIds}
                selectMultiple
              />
            </Flex>
          </Tabs.Panel>

          <Tabs.Panel value="fund" flex={1} mih={0}>
            <Flex mt={'md'} flex={1} mih={0} direction="column">
              <SelectableItemList
                items={tabGroups.fundFactSheets}
                selectedItemIds={selectedFileIds}
                setSelectedItemIds={setSelectedFileIds}
                selectMultiple
              />
            </Flex>
          </Tabs.Panel>

          <Tabs.Panel value="company" flex={1} mih={0}>
            <Flex mt={'md'} flex={1} mih={0} direction="column">
              <SelectableItemList
                items={tabGroups.companyDocuments}
                selectedItemIds={selectedFileIds}
                setSelectedItemIds={setSelectedFileIds}
                selectMultiple
              />
            </Flex>
          </Tabs.Panel>

          <Tabs.Panel value="other" flex={1} mih={0}>
            <Flex mt={'md'} flex={1} mih={0} direction="column">
              <SelectableItemList
                items={tabGroups.other}
                selectedItemIds={selectedFileIds}
                setSelectedItemIds={setSelectedFileIds}
                selectMultiple
              />
            </Flex>
          </Tabs.Panel>
        </Tabs>

        <Flex direction={'column'} style={{ flexShrink: 0 }}>
          <Text size="textSm" c="black" data-testid="document-count">
            <Text fw={FONT_WEIGHTS.bold} span size="textSm" c="black" data-testid="document-count">
              {"Can't find the document? "}
            </Text>
            <InlineClickableText>
              {'Upload '}
              <input
                type="file"
                multiple
                accept=".pdf,.docx"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
            </InlineClickableText>
          </Text>
          <Text size={'xs'} fw={FONT_WEIGHTS.medium} c={'gray.7'}>
            {
              "The documents you upload here won't be saved to the platform and appear only as appendices in this suitability report."
            }
          </Text>
        </Flex>
      </Flex>
    </DrawerFlowScreenLayout>
  );
}
